import type { Moment } from './moment'
import moment from './moment'

export type CacheOptions = {
  expires?: Moment
}

export class Cache<T = any, K = string> {
  data: Map<K, { expires?: Moment; data: any }>

  constructor(initial?: Map<K, { expires?: Moment; data: any }>) {
    this.data = initial ?? new Map()
  }

  set(key: K, data: T, { expires = moment().add(5, 'minutes') }: CacheOptions = {}) {
    this.data.set(key, { expires, data })
  }

  get(key: K): T | null {
    const item = this.data.get(key)
    if (!item) return null
    if (item.expires && item.expires.isSameOrBefore(moment())) {
      this.data.delete(key)
      return null
    }
    return item.data
  }

  upsert(key: K, getter: () => T, options: CacheOptions = {}): T {
    if (!this.has(key)) {
      this.set(key, getter(), options)
    }
    // Not get(): its second expiry check can lapse between the has() clock
    // read and this one, answering null despite the freshness check above.
    return this.data.get(key)!.data
  }

  async upsertAsync(key: K, getter: () => Promise<T>, options: CacheOptions = {}): Promise<T> {
    if (!this.has(key)) {
      this.set(key, await getter(), options)
    }
    return this.data.get(key)!.data
  }

  has(key: K): boolean {
    const item = this.data.get(key)
    if (!item) return false
    if (item.expires && item.expires.isSameOrBefore(moment())) {
      this.data.delete(key)
      return false
    }
    return true
  }

  remove(key: K) {
    this.data.delete(key)
  }
}

export const withCache = <T, K>(cache: Cache<any, K> | undefined, key: K, getter: () => Promise<T>, options?: CacheOptions) => {
  if (!cache) return getter()
  return (cache as Cache<T, K>).upsertAsync(key, getter, options)
}
